import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import {
  Anchored,
  CARD_SPACING,
  ESTIMATED_CARD_HEIGHT,
  calculatePositionsForRender,
  calculateRawPositions,
  sortItemsByPosition,
  updateLockedOffsets,
} from "@components/QaReview/ReviewStack/positioning";

export interface UseAnchoredStackOptions {
  containerId: string;
  scrollRef?: React.RefObject<HTMLDivElement | null>;
  activeId: string | null;
  /**
   * A slot pinned to the top of the stack, outside `items` (e.g. the QA scan's error box). Register it
   * with `registerRef` so it is measured; no card is placed above its bottom.
   */
  topSlotId?: string | null;
}

export interface AnchoredStackResult<T extends Anchored> {
  order: T[];
  rawPositions: Record<string, number>;
  positions: Record<string, number>;
  heights: Record<string, number>;
  validIds: Set<string>;
  /** A measuring pass has run on the current items, so an empty order means none of them is on the page. */
  isMeasured: boolean;
  registerRef: (id: string, el: HTMLDivElement | null) => void;
}

/**
 * Owns the ResizeObserver/MutationObserver/scroll-listener wiring behind the raw-position
 * calculation, generalized from Suggestions.tsx's own effect (see Task 6's rewrite for the 1:1
 * mapping from its old local state to this hook's return values).
 */
export const useAnchoredStack = <T extends Anchored>(
  items: T[],
  { containerId, scrollRef, activeId, topSlotId = null }: UseAnchoredStackOptions,
): AnchoredStackResult<T> => {
  const [rawPositions, setRawPositions] = useState<Record<string, number>>({});
  const [lockedOffsets, setLockedOffsets] = useState<Record<string, number>>({});
  const [validIds, setValidIds] = useState<Set<string>>(() => new Set());
  const [heights, setHeights] = useState<Record<string, number>>({});
  const [measuredItems, setMeasuredItems] = useState<T[] | null>(null);
  const nodesRef = useRef<Record<string, HTMLDivElement | null>>({});
  const resizeObserverRef = useRef<ResizeObserver | null>(null);

  const measureHeights = useCallback(() => {
    setHeights((prev) => {
      const next: Record<string, number> = {};
      Object.entries(nodesRef.current).forEach(([id, el]) => {
        // A culled (display: none) card reads 0 — keep its last real height rather than falling
        // back to the estimate, which would let its neighbours overlap it when it scrolls back in.
        const height = el?.offsetHeight || prev[id];
        if (height) next[id] = height;
      });
      const same =
        Object.keys(prev).length === Object.keys(next).length && Object.keys(next).every((k) => prev[k] === next[k]);
      return same ? prev : next;
    });
  }, []);

  // A card can change height without the item list changing (reply composer opening, replies or
  // "Show more" expanding, a card collapsing to its resolved summary). Re-measuring on resize is
  // what keeps neighbours from overlapping a card that grew after the last measurement.
  useEffect(() => {
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measureHeights);
    resizeObserverRef.current = observer;
    Object.values(nodesRef.current).forEach((el) => el && observer.observe(el));
    return () => {
      observer.disconnect();
      resizeObserverRef.current = null;
    };
  }, [measureHeights]);

  useEffect(() => {
    let rafId = 0;

    if (!items.length) {
      rafId = requestAnimationFrame(() => {
        setRawPositions({});
        setValidIds(new Set());
        setMeasuredItems(items);
      });
      return () => cancelAnimationFrame(rafId);
    }

    const update = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const { positions: newPos, visibleMap, validItemIds } = calculateRawPositions(items, containerId);

        setRawPositions((prev) => {
          const same =
            Object.keys(newPos).length === Object.keys(prev).length &&
            Object.keys(newPos).every((k) => prev[k] === newPos[k]);
          return same ? prev : newPos;
        });

        setValidIds((prev) => {
          const prevArr = Array.from(prev).sort();
          const newArr = Array.from(validItemIds).sort();
          const same = prevArr.length === newArr.length && prevArr.every((id, i) => id === newArr[i]);
          return same ? prev : validItemIds;
        });

        setLockedOffsets((prev) => updateLockedOffsets(items, visibleMap, newPos, prev));
        setMeasuredItems(items);

        rafId = 0;
      });
    };

    update();

    const leftContainer = document.getElementById(containerId);
    leftContainer?.addEventListener("scroll", update, { passive: true });
    window.addEventListener("scroll", update, { passive: true });
    window.addEventListener("resize", update);
    const right = scrollRef?.current;
    right?.addEventListener("scroll", update, { passive: true });

    let mutationObserver: MutationObserver | null = null;
    if (leftContainer) {
      mutationObserver = new MutationObserver(() => update());
      mutationObserver.observe(leftContainer, { childList: true, subtree: true });
    }

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      leftContainer?.removeEventListener("scroll", update);
      window.removeEventListener("scroll", update);
      window.removeEventListener("resize", update);
      right?.removeEventListener("scroll", update);
      mutationObserver?.disconnect();
    };
    // activeId is included so a row that expands/collapses as a result of activating an item
    // gets picked up by a fresh measurement pass, matching Suggestions.tsx's own effect today
    // (which depended on activeSuggestionFieldId).
  }, [items, scrollRef, containerId, activeId]);

  const order = useMemo(() => {
    const filtered = items.filter((item) => validIds.has(item.id));
    return sortItemsByPosition(filtered, rawPositions);
  }, [items, rawPositions, validIds]);

  useEffect(() => {
    const rafId = requestAnimationFrame(measureHeights);
    return () => cancelAnimationFrame(rafId);
  }, [order, measureHeights]);

  const minTop = topSlotId ? (heights[topSlotId] || ESTIMATED_CARD_HEIGHT) + CARD_SPACING : 0;
  const positions = useMemo(
    () =>
      calculatePositionsForRender(
        order,
        rawPositions,
        heights,
        lockedOffsets,
        activeId,
        CARD_SPACING,
        ESTIMATED_CARD_HEIGHT,
        minTop,
      ),
    [order, rawPositions, heights, lockedOffsets, activeId, minTop],
  );

  const registerRef = (id: string, el: HTMLDivElement | null) => {
    const prev = nodesRef.current[id];
    if (prev === el) return;
    if (prev) resizeObserverRef.current?.unobserve(prev);
    nodesRef.current[id] = el;
    if (el) resizeObserverRef.current?.observe(el);
  };

  return { order, rawPositions, positions, heights, validIds, isMeasured: measuredItems === items, registerRef };
};
